import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  Button,
  TextField,
  Divider,
  IconButton,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Phone,
  Work,
  Group,
  Restaurant,
  AttachMoney
} from '@mui/icons-material';
import MainService from '../services/MainService';

const MyProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileData, setProfileData] = useState({
    Name: '',
    EmailId: '',
    MobileNo: '',
    AltMobileNo: '',
    FoodHabit: '',
    NoOfFamilyMembers: '',
    Money: '',
    Profession: ''
  });

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem('user') || '{}');
  const customerId = userData.CustomerId;

  const loadProfile = async () => {
    if (!customerId) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await MainService.getCustomerById(customerId);
      if (response) {
        setProfileData({
          Name: response.Name || '',
          EmailId: response.EmailId || '',
          MobileNo: response.MobileNo || '',
          AltMobileNo: response.AltMobileNo || '',
          FoodHabit: response.FoodHabit || '',
          NoOfFamilyMembers: response.NoOfFamilyMembers || '',
          Money: response.Money || '',
          Profession: response.Profession || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfile();
  }, []);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!customerId) {
      setError('Customer ID not found. Please login again.');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {
        Id: customerId,
        MobileNo: profileData.MobileNo, // Current customer mobile no
        EmailId: profileData.EmailId,
        Name: profileData.Name,
        Profession: profileData.Profession,
        NoOfFamilyMembers: profileData.NoOfFamilyMembers,
        AltMobileNo: profileData.AltMobileNo,
        FoodHabit: profileData.FoodHabit,
        Money: profileData.Money
      };

      const response = await MainService.updateCustomerData(updateData);
      
      if (response === true || response === 1) {
        setSuccess('Profile updated successfully!');
        setIsEditing(false);
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadProfile(); // Reload original data
  };

  const handleChange = (field) => (event) => {
    setProfileData({
      ...profileData,
      [field]: event.target.value
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#2E7D32' }} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
        My Profile
      </Typography>
      <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
        Manage your account information
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Profile Card */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  Personal Information
                </Typography>
                <IconButton
                  onClick={isEditing ? handleSave : handleEdit}
                  color="primary"
                  disabled={saving}
                  sx={{
                    backgroundColor: '#2E7D32',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1B5E20',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(46, 125, 50, 0.7)',
                    },
                  }}
                >
                  {saving ? (
                    <CircularProgress size={20} sx={{ color: 'white' }} />
                  ) : isEditing ? (
                    <Save />
                  ) : (
                    <Edit />
                  )}
                </IconButton>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={profileData.Name}
                    onChange={handleChange('Name')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={profileData.EmailId}
                    onChange={handleChange('EmailId')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={profileData.MobileNo}
                    disabled={true} // Phone number cannot be edited
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    sx={{
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Alternative Phone Number"
                    value={profileData.AltMobileNo}
                    onChange={handleChange('AltMobileNo')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Profession"
                    value={profileData.Profession}
                    onChange={handleChange('Profession')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Work sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Number of Family Members"
                    type="number"
                    value={profileData.NoOfFamilyMembers}
                    onChange={handleChange('NoOfFamilyMembers')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <Group sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!isEditing}>
                    <InputLabel>Food Habit</InputLabel>
                    <Select
                      value={profileData.FoodHabit}
                      onChange={handleChange('FoodHabit')}
                      label="Food Habit"
                      startAdornment={<Restaurant sx={{ mr: 1, color: 'text.secondary' }} />}
                    >
                      <MenuItem value="Vegetarian">Vegetarian</MenuItem>
                      <MenuItem value="Non-Vegetarian">Non-Vegetarian</MenuItem>
                      <MenuItem value="Vegan">Vegan</MenuItem>
                      <MenuItem value="Jain">Jain</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Monthly Budget"
                    value={profileData.Money}
                    onChange={handleChange('Money')}
                    disabled={!isEditing}
                    InputProps={{
                      startAdornment: <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                    placeholder="e.g., 5000"
                  />
                </Grid>
              </Grid>

              {isEditing && (
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                      borderColor: '#2E7D32',
                      color: '#2E7D32',
                      '&:hover': {
                        borderColor: '#1B5E20',
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Summary */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Avatar
              sx={{ 
                width: 120, 
                height: 120, 
                mx: 'auto', 
                mb: 3,
                backgroundColor: '#2E7D32',
                fontSize: '3rem'
              }}
            >
              {profileData.Name?.charAt(0)?.toUpperCase() || 'U'}
            </Avatar>
            <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
              {profileData.Name || 'User'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {profileData.EmailId}
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                <Typography variant="body2">{profileData.MobileNo}</Typography>
              </Box>
              {profileData.AltMobileNo && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{profileData.AltMobileNo}</Typography>
                </Box>
              )}
              {profileData.Profession && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Work sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{profileData.Profession}</Typography>
                </Box>
              )}
              {profileData.FoodHabit && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Restaurant sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{profileData.FoodHabit}</Typography>
                </Box>
              )}
              {profileData.NoOfFamilyMembers && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Group sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">{profileData.NoOfFamilyMembers} family members</Typography>
                </Box>
              )}
              {profileData.Money && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AttachMoney sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">₹{profileData.Money} monthly budget</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MyProfile; 